import { Card, CardTitle } from "@/components/Card";
import { FieldRow, RowListField, SelectField } from "@/components/strategy/FieldEditors";
import TemplateTextField from "@/components/strategy/TemplateTextField";
import { templateVariables } from "@/lib/phaseVocabulary";
import { usePhaseEditor, type PhaseEditorProps } from "@/components/strategy/phaseEditing";

/**
 * How the email for this phase comes about. Three modes over two YAML keys:
 *
 * - agent  — no `email_template`; the model writes from the method.
 * - fixed  — `email_template` sent as written, no model call
 *            (`email_template_mode` absent or `fixed`).
 * - draft  — `email_template_mode: draft`; the model receives the rendered
 *            script as the draft to send and adapts it to the conversation.
 *
 * `email_examples` are patterns the model may follow or depart from whenever
 * it writes (agent or draft mode); a fixed script ignores them.
 */
type Mode = "agent" | "fixed" | "draft";

const MODE_OPTIONS: { value: Mode; label: string }[] = [
  { value: "agent", label: "Written by the agent" },
  { value: "fixed", label: "Fixed script — sent as written, no AI" },
  { value: "draft", label: "Draft — the agent adapts it to the conversation" },
];

const MODE_HINT: Record<Mode, string> = {
  agent: "The agent composes each email from the phase goal, the knowledge base and the rules below.",
  fixed: "Sent exactly as written after filling in the variables. No model call, so nothing can change the wording. Use for legal or confirmation messages.",
  draft: "The agent starts from this text and changes only what the conversation requires — it keeps the substance, structure and any commitment the script makes.",
};

export default function PhaseEmailCard(props: PhaseEditorProps) {
  const { phase, str, list, edit, isEdited } = usePhaseEditor(props);
  const template = str(["email_template"]);
  const modeKey = str(["email_template_mode"]);
  const mode: Mode =
    modeKey?.toLowerCase() === "draft" ? "draft"
    : modeKey?.toLowerCase() === "fixed" || template !== null ? "fixed"
    : "agent";
  const variables = templateVariables(props.document, phase);
  const examples = list<string>(["email_examples"]);

  const setMode = (next: Mode) => {
    if (next === "agent") {
      edit(["email_template"], null);
      edit(["email_template_mode"], null);
      return;
    }
    edit(["email_template_mode"], next);
  };

  return (
    <Card>
      <CardTitle>Email for this phase</CardTitle>
      <div className="space-y-3">
        <FieldRow
          label="How it is written"
          edited={isEdited(["email_template_mode"]) || isEdited(["email_template"])}
          hint={MODE_HINT[mode]}
        >
          <SelectField
            ariaLabel="Email mode"
            value={mode}
            options={MODE_OPTIONS}
            onCommit={(v) => setMode((v as Mode | null) ?? "agent")}
          />
        </FieldRow>

        {mode !== "agent" ? (
          <FieldRow
            label={mode === "fixed" ? "Script" : "Draft"}
            edited={isEdited(["email_template"])}
            hint="First line `SUBJECT: …` sets the subject. Variables in double braces are filled per prospect; the save is refused if one is unknown."
          >
            <TemplateTextField
              ariaLabel="Email script"
              value={template}
              variables={variables}
              placeholder={"SUBJECT: …\n\nHi {{prospect.first_name}}, …"}
              onCommit={(v) => edit(["email_template"], v)}
            />
          </FieldRow>
        ) : null}

        <FieldRow
          label="Example emails"
          edited={isEdited(["email_examples"])}
          hint={
            mode === "fixed"
              ? "Not used while the script is fixed — the agent does not write in this phase."
              : "Patterns that work in this phase. The agent may follow one when it fits or write its own using the method; it never copies prospect details from an example."
          }
        >
          <RowListField<string>
            value={examples}
            rowLabel="example"
            addLabel="Add example"
            emptyText="none — the agent writes from the method alone"
            newRow={() => ""}
            disabled={mode === "fixed" && (examples ?? []).length === 0}
            onCommit={(rows) => edit(["email_examples"], rows)}
            renderRow={(row, update, i) => (
              <TemplateTextField
                ariaLabel={`Example email ${i + 1}`}
                value={row}
                rows={5}
                variables={variables}
                onCommit={(v) => update(v ?? "")}
              />
            )}
          />
        </FieldRow>
      </div>
    </Card>
  );
}
